import { flow, identity } from 'fp-ts/function';
import { map, spread, add } from 'lodash/fp';
import { createHandler } from '@mews/std';
import {
    DateService,
    DateTimePart,
    DateTimeService,
    DateTimeUtils,
    TimeService,
    TimeUnitUtils,
    WallClockDateService,
} from '@mews/framework';
import { StructuredDate } from '@optimus-web/core';
import { ifElse } from '@mews/fp';
import { BreakpointRange } from '@mews-commander/ui';

export const getNumberOfMonths = createHandler(
    {
        [BreakpointRange.Phone.name]: 1,
        [BreakpointRange.Tablet.name]: 2,
    },
    3
);

const getTimeUnitCount = ({ timePart, greedy = false }: { timePart: DateTimePart; greedy?: boolean }) =>
    flow(
        map(flow(DateTimeUtils.structuredDateToISOString, WallClockDateService.parse)),
        ifElse(
            () => greedy,
            ([end, start]) => [
                WallClockDateService.startOf(timePart)(end),
                WallClockDateService.startOf(timePart)(start),
            ],
            identity
        ),
        spread(WallClockDateService.diff(timePart)),
        ifElse(() => greedy, add(1), identity)
    );

interface GetTimeUnitsCountsParams {
    endDate?: StructuredDate | null;
    startDate?: StructuredDate | null;
}

export const getTimeUnitsCounts = ({ startDate, endDate }: GetTimeUnitsCountsParams) => {
    if (!startDate || !endDate) {
        return {
            hourCount: 0,
            monthCount: 0,
            nightCount: 0,
        };
    }

    return {
        hourCount: getTimeUnitCount({ timePart: DateTimePart.Hour, greedy: false })([endDate, startDate]),
        monthCount: getTimeUnitCount({ timePart: DateTimePart.Month, greedy: true })([endDate, startDate]),
        nightCount: getTimeUnitCount({ timePart: DateTimePart.Day, greedy: false })([endDate, startDate]),
    };
};

export const getHoursCount = ({
    startDate,
    endDate,
    startTime,
    endTime,
    timezone,
}: {
    startDate?: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    timezone: string;
}) => {
    if (!startDate || !endDate || !startTime || !endTime) {
        return 0;
    }
    const parsedStartDate = DateService.parse(startDate, timezone);
    const parsedEndDate = DateService.parse(endDate, timezone);
    const parsedStartTime = TimeService.parse(startTime);
    const parsedEndTime = TimeService.parse(endTime);
    const start = DateTimeService.fromDateAndTime(parsedStartDate, parsedStartTime);
    const end = DateTimeService.fromDateAndTime(parsedEndDate, parsedEndTime);

    const greedy = TimeUnitUtils.isGreedyHours(start, end);
    return TimeUnitUtils.dateTimeToTimeUnitCount({ timePart: DateTimePart.Hour, greedy })(start, end);
};
